/**
 * PATH: packages/ui/src/kanban/components/KanbanCardEmailMeta.tsx
 * FILE_ID: UI-KANBAN-CARD-EMAILMETA-001
 * PURPOSE: The expanded-density email-meta row — sender avatar (hashed colour +
 *   initials), sender name/address, and the received date chip.
 * WHY: Extracted from KanbanCard.tsx (≤500-line working limit). Pure: resolved
 *   email meta in, row out; renders nothing when there's no displayable field
 *   (matches the former inline guard so the DOM is unchanged).
 * RELATED:
 *   - ./KanbanCard.tsx (renders this only in expanded density).
 *   - ./kanbanCardVisuals (pickHashedColor / getMailboxInitials / formatCardDate).
 *   - ../api KanbanEmailCardMeta (the resolved shape).
 */
import type { KanbanEmailCardMeta } from '../api'
import { readableForeground } from '../utils'
import { formatCardDate, getMailboxInitials, pickHashedColor } from './kanbanCardVisuals'

export interface KanbanCardEmailMetaProps {
  meta: KanbanEmailCardMeta
  showSender?: boolean
  showReceived?: boolean
}

export function KanbanCardEmailMeta({
  meta,
  showSender = true,
  showReceived = true,
}: KanbanCardEmailMetaProps) {
  const hasSender = showSender && !!(meta.from_name || meta.from_address)
  const hasReceived = showReceived && !!meta.received_at
  if (!hasSender && !hasReceived) return null

  const senderLabel = meta.from_name || meta.from_address || ''
  const titleAttr =
    meta.from_name && meta.from_address
      ? `${meta.from_name} <${meta.from_address}>`
      : senderLabel

  return (
    <div
      className="mt-1.5 flex items-center gap-1.5 text-[10.5px] text-neutral-600 dark:text-zinc-300"
      style={{ color: 'var(--card-fg-muted)' }}
    >
      {/* SENDER avatar (qf!2666 — operator complaint 2026-05-08:
          "I should be able to see profile from which email it
          came from in a circular way"). Hashes on the SENDER's
          address (not the destination mailbox) and renders big
          enough to read the initials. The destination mailbox dot
          lives in the footer right-group. */}
      {hasSender &&
        (() => {
          const senderKey = (meta.from_address || meta.from_name || '').toLowerCase()
          const senderColor = pickHashedColor(`from:${senderKey}`)
          const fg = readableForeground(senderColor)
          return (
            <span
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold leading-none ring-1 ring-white/40 dark:ring-zinc-900/40"
              style={{ background: senderColor, color: fg }}
              title={titleAttr}
              aria-label={`Sender ${senderLabel}`}
            >
              {getMailboxInitials(senderLabel)}
            </span>
          )
        })()}
      {hasSender && (
        <span
          className="truncate min-w-0 flex-1 font-medium text-neutral-700 dark:text-zinc-200"
          title={
            meta.from_name && meta.from_address
              ? `${meta.from_name} <${meta.from_address}>`
              : meta.from_name || meta.from_address
          }
        >
          {meta.from_name || meta.from_address}
        </span>
      )}
      {hasReceived && meta.received_at && (
        <span className="shrink-0 text-neutral-500 dark:text-zinc-400 tabular-nums">
          {formatCardDate(meta.received_at)}
        </span>
      )}
      {/* Mailbox indicator was here as a tiny 3×3 dot — promoted to a
          readable mailbox chip in the card's footer right-group (see
          KanbanCardFooter). */}
    </div>
  )
}
